import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const PrintroomControl = ({ jobId, orderNumber, quoteName, onNotificationSent = () => { } }) => {
  const [isReady, setIsReady] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [readinessData, setReadinessData] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [showForceSendConfirmation, setShowForceSendConfirmation] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const { isAuthenticated } = useAuth();

  const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';

  const checkReadiness = async () => {
    if (!jobId || !orderNumber) return;

    setIsChecking(true);
    setLastResult(null);

    try {
      const response = await fetch(`${UPLOAD_API_URL}/api/printroom/check-readiness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          order_number: orderNumber
        })
      });

      const data = await response.json();
      if (response.ok) {
        setReadinessData({
          ready: data.ready || false,
          has_jobsheet: data.has_jobsheet,
          has_artwork: data.has_artwork,
          total_files: data.total_files,
          has_printlogic_order: data.has_printlogic_order,
          format_type: data.format_type,
          error: data.error
        });
        setNotificationStatus({
          notification_sent: data.notification_sent || false,
          notification_count: data.notification_count || 0,
          last_notification: data.last_notification,
          notifications: data.notifications || []
        });
        setIsReady(data.ready || false);
      } else {
        console.error('Readiness check failed:', data);
        setReadinessData({
          ready: false,
          error: data.message || `HTTP ${response.status}`
        });
        setNotificationStatus({
          notification_sent: false,
          notification_count: 0,
          last_notification: null,
          notifications: []
        });
        setIsReady(false);
      }
    } catch (error) {
      console.error('Error checking readiness:', error);
      const errorMessage = error.message.includes('fetch')
        ? 'Network error - please check your connection'
        : 'Error checking readiness - please try again';
      setReadinessData({
        ready: false,
        error: errorMessage
      });
      setIsReady(false);
    } finally {
      setIsChecking(false);
    }
  };

  const triggerPrintroom = async (forceResend = false, skipArtworkCheck = false) => {
    setIsTriggering(true);
    setLastResult(null);

    try {
      const response = await fetch(`${UPLOAD_API_URL}/api/printroom/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          order_number: orderNumber,
          trigger_type: 'manual',
          force_resend: forceResend,
          skip_artwork_check: skipArtworkCheck
        })
      });

      const result = await response.json();
      setLastResult(result);

      if (result.success) {
        onNotificationSent && onNotificationSent(result);
        // Recheck readiness after successful trigger
        setTimeout(checkReadiness, 1000);
      }
    } catch (error) {
      console.error('Error triggering printroom:', error);
      const errorMessage = error.message.includes('fetch')
        ? 'Network error - please check your connection and try again'
        : 'Error sending notification - please try again';
      setLastResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setIsTriggering(false);
      setShowConfirmation(false);
      setShowResendConfirmation(false);
      setShowForceSendConfirmation(false);
    }
  };

  useEffect(() => {
    if (jobId && orderNumber && isAuthenticated) {
      checkReadiness();
    }
  }, [jobId, orderNumber, isAuthenticated]);

  if (!orderNumber) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-medium text-yellow-900 mb-2">Printroom Control</h3>
        <p className="text-yellow-800 text-sm">
          No PrintLogic order number found. Quote must be submitted to PrintLogic first.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-blue-900">Printroom Control</h3>
        <button
          onClick={checkReadiness}
          disabled={isChecking}
          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 px-2 py-1 rounded hover:bg-blue-100"
        >
          {isChecking ? 'Checking...' : 'Check Status'}
        </button>
      </div>

      {/* Order Information */}
      <div className="mb-3 text-sm text-blue-800">
        <p><strong>Order:</strong> {orderNumber}</p>
        <p><strong>Job:</strong> {quoteName || `${jobId?.substring(0, 8)}...`}</p>
      </div>

      {/* Status Display */}
      {isChecking ? (
        <div className="flex items-center text-blue-700 text-sm">
          <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
          Checking readiness...
        </div>
      ) : readinessData ? (
        <div className="space-y-3">
          {/* Readiness Criteria */}
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-blue-800">Jobsheet Present:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${readinessData.has_jobsheet
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                }`}>
                {readinessData.has_jobsheet ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-blue-800">Artwork Present:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${readinessData.has_artwork
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                }`}>
                {readinessData.has_artwork ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            {readinessData.total_files !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-blue-800">Total Files:</span>
                <span className="text-blue-900 font-medium">{readinessData.total_files}</span>
              </div>
            )}
          </div>

          {/* Status Messages */}
          {readinessData.error && (
            <div className={`text-sm rounded p-2 ${readinessData.error.includes('PrintLogic order required')
                ? 'text-orange-700 bg-orange-50 border border-orange-200'
                : 'text-red-700 bg-red-50 border border-red-200'
              }`}>
              <strong>
                {readinessData.error.includes('PrintLogic order required') ? 'Note:' : 'Error:'}
              </strong> {readinessData.error}
            </div>
          )}

          {/* PrintLogic Status Info */}
          {readinessData.has_printlogic_order !== undefined && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-800">PrintLogic Order:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${readinessData.has_printlogic_order
                  ? 'bg-green-100 text-green-800'
                  : 'bg-orange-100 text-orange-800'
                }`}>
                {readinessData.has_printlogic_order ? '✓ Yes' : '⚠ Required'}
              </span>
            </div>
          )}

          {/* Format Type Info */}
          {readinessData.format_type && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-800">Format:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${readinessData.format_type === 'small-format'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>
                {readinessData.format_type === 'small-format' ? 'Small Format' : 'Large Format'}
              </span>
            </div>
          )}

          {/* Notification Status Display */}
          {notificationStatus?.notification_sent && (
            <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2 mb-3">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Sent to Printroom
                </span>
                <span className="text-sm text-green-700">
                  {notificationStatus.notification_count} time{notificationStatus.notification_count !== 1 ? 's' : ''}
                </span>
              </div>

              {notificationStatus.last_notification && (
                <div className="text-sm text-green-700">
                  <p><strong>Last sent:</strong> {new Date(notificationStatus.last_notification.iso_timestamp).toLocaleString()}</p>
                  <p><strong>Trigger:</strong> {notificationStatus.last_notification.trigger_type}</p>
                </div>
              )}

              {/* Notification History */}
              {notificationStatus.notifications && notificationStatus.notifications.length > 1 && (
                <details className="text-sm">
                  <summary className="text-green-800 cursor-pointer font-medium">View notification history ({notificationStatus.notifications.length})</summary>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {notificationStatus.notifications.slice().reverse().map((notification, index) => (
                      <div key={index} className="text-xs text-green-600 border-l-2 border-green-300 pl-2">
                        {new Date(notification.iso_timestamp).toLocaleString()} - {notification.trigger_type}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Action Button */}
          {isReady ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Ready for Printroom
                </span>
              </div>

              {/* First-time send or resend options */}
              {!notificationStatus?.notification_sent ? (
                !showConfirmation ? (
                  <button
                    onClick={() => setShowConfirmation(true)}
                    disabled={isTriggering}
                    className="w-full px-4 py-2 rounded font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300"
                  >
                    Send to {readinessData.format_type === 'small-format' ? 'Printroom' : 'Wide Format Team'}
                  </button>
                ) : (
                  <div className="bg-white border border-green-200 rounded p-3 space-y-2">
                    <p className="text-sm font-medium text-green-800">
                      Send this job to the {readinessData.format_type === 'small-format' ? 'printroom' : 'wide format team'}?
                    </p>
                    <p className="text-xs text-green-700">
                      This will notify the {readinessData.format_type === 'small-format' ? 'printroom team' : 'wide format team'} that the job is ready for production.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => triggerPrintroom(false)}
                        disabled={isTriggering}
                        className="flex-1 px-3 py-1 text-sm rounded font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300"
                      >
                        {isTriggering ? 'Sending...' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setShowConfirmation(false)}
                        disabled={isTriggering}
                        className="flex-1 px-3 py-1 text-sm rounded font-medium text-gray-600 bg-gray-200 hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              ) : (
                // Manager resend option
                !showResendConfirmation ? (
                  <button
                    onClick={() => setShowResendConfirmation(true)}
                    disabled={isTriggering}
                    className="w-full px-4 py-2 rounded font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300"
                  >
                    Force Resend to Printroom
                  </button>
                ) : (
                  <div className="bg-white border border-orange-200 rounded p-3 space-y-2">
                    <p className="text-sm font-medium text-orange-800">
                      Force resend notification to printroom?
                    </p>
                    <p className="text-xs text-orange-700">
                      This will send another notification even though one was already sent. Use this if there was an issue with the previous notification.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => triggerPrintroom(true)}
                        disabled={isTriggering}
                        className="flex-1 px-3 py-1 text-sm rounded font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300"
                      >
                        {isTriggering ? 'Resending...' : 'Force Resend'}
                      </button>
                      <button
                        onClick={() => setShowResendConfirmation(false)}
                        disabled={isTriggering}
                        className="flex-1 px-3 py-1 text-sm rounded font-medium text-gray-600 bg-gray-200 hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="text-orange-700 text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mb-2">
                ⚠ Not Ready
              </span>
              <p className="mb-3">Missing required files. Job cannot be sent to printroom yet.</p>

              {/* Force Send option - only show if has jobsheet but no artwork */}
              {readinessData?.has_jobsheet && !readinessData?.has_artwork && readinessData?.has_printlogic_order && (
                <div className="mt-3 pt-3 border-t border-orange-200">
                  {!showForceSendConfirmation ? (
                    <button
                      onClick={() => setShowForceSendConfirmation(true)}
                      disabled={isTriggering}
                      className="w-full px-4 py-2 rounded font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300"
                    >
                      ⚡ Force Send (Jobsheet Only)
                    </button>
                  ) : (
                    <div className="bg-white border border-amber-300 rounded p-3 space-y-2">
                      <p className="text-sm font-medium text-amber-800">
                        ⚠️ Send without artwork?
                      </p>
                      <p className="text-xs text-amber-700">
                        This will notify the printroom with only the jobsheet - no artwork files are present in the folder.
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => triggerPrintroom(false, true)}
                          disabled={isTriggering}
                          className="flex-1 px-3 py-1 text-sm rounded font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300"
                        >
                          {isTriggering ? 'Sending...' : 'Confirm Force Send'}
                        </button>
                        <button
                          onClick={() => setShowForceSendConfirmation(false)}
                          disabled={isTriggering}
                          className="flex-1 px-3 py-1 text-sm rounded font-medium text-gray-600 bg-gray-200 hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-blue-700 text-sm">Unable to check printroom status</p>
      )}

      {/* Result Display */}
      {lastResult && (
        <div className={`mt-3 p-2 rounded text-sm border ${lastResult.success
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }`}>
          <strong>{lastResult.success ? 'Success:' : 'Error:'}</strong> {lastResult.message}
        </div>
      )}
    </div>
  );
};

export default PrintroomControl;